import {
  UPLOADER_STATUS_FAILED,
  UPLOADER_STATUS_PAUSED,
  UPLOADER_STATUS_UPLOADING,
  UPLOADER_STATUS_WAITING,
  type UploaderState,
} from '@iconik-io/uploader/src/state';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import {
  UPLOADER_ACLS_MODAL,
  UPLOADER_CANCEL_UPLOAD_MODAL,
  UPLOADER_EDIT_METADATA_MODAL,
  UPLOADER_EDIT_TITLE_MODAL,
  UPLOADER_UPLOAD_FILE_INFO,
  UPLOADER_UPLOAD_STORAGE_MODAL,
} from '../../constants/modals';
import { SHARE_COLLECTION_PAGE_ROUTE } from '../../constants/routes';
import {
  CONTEXT_MENU_UPLOADER_ITEMS,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  pauseUploads,
  removeUploads,
  resumeUploads,
} from '../../ducks/uploader/engine/thunks';
import {
  isNewAssetUpload,
  isPausedUpload,
} from '../../ducks/uploader/upload_state';
import { ICONS } from '../../icons/icon';
import type { AppDispatch } from '../../store/types';
import { selectUserRoles } from '../../utils/get_user_roles';
import { selectStorageOptionDisabled } from './selectors';

const isRemovable = (upload: UploaderState): boolean =>
  upload.status !== UPLOADER_STATUS_UPLOADING;

const isNotProcessed = (upload: UploaderState): boolean =>
  upload.status === UPLOADER_STATUS_FAILED ||
  upload.status === UPLOADER_STATUS_WAITING ||
  upload.status === UPLOADER_STATUS_PAUSED;

const isCancellable = (upload: UploaderState): boolean =>
  upload.status === UPLOADER_STATUS_UPLOADING || isPausedUpload(upload);

const isPausable = (upload: UploaderState): boolean =>
  upload.status === UPLOADER_STATUS_UPLOADING ||
  upload.status === UPLOADER_STATUS_WAITING;

const getDefinition: GetDefinition<UploaderState> = (
  { contextItems },
  state,
) => {
  const items: ContextMenuItemDefinition[] = [];
  const isShare = state.routeParams.route === SHARE_COLLECTION_PAGE_ROUTE;
  const userRoles = selectUserRoles(state);

  const canRemove = contextItems.every(isRemovable);

  const canCancel = contextItems.every(isCancellable);

  const canChangeStorage =
    !selectStorageOptionDisabled(state) && contextItems.every(isNotProcessed);

  const filesAreNewAssetUploads = contextItems.every(isNewAssetUpload);

  const canAddMetadataToFiles =
    userRoles.canReadMetadataViews &&
    userRoles.canWriteMetadataValues &&
    filesAreNewAssetUploads;

  items.push({
    multiple: false,
    label: 'Edit Title…',
    icon: ICONS.Edit,
    action: () => (dispatch: AppDispatch) => {
      dispatch(openModal({ id: UPLOADER_EDIT_TITLE_MODAL, contextItems }));
    },
  });

  if (canAddMetadataToFiles && !isShare) {
    items.push({
      multiple: true,
      label: 'Set Metadata…',
      icon: ICONS.Metadata,
      action: () => (dispatch: AppDispatch) => {
        dispatch(openModal({ id: UPLOADER_EDIT_METADATA_MODAL, contextItems }));
      },
    });
  }

  if (filesAreNewAssetUploads && userRoles.canWriteAcls && !isShare) {
    items.push({
      multiple: true,
      label: 'Add Permissions…',
      icon: ICONS.Permission,
      action: () => (dispatch: AppDispatch) =>
        dispatch(openModal({ id: UPLOADER_ACLS_MODAL, contextItems })),
    });
  }

  if (canChangeStorage && !isShare) {
    items.push({
      multiple: true,
      label: 'Change destination…',
      icon: ICONS.Cloud,
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({ id: UPLOADER_UPLOAD_STORAGE_MODAL, contextItems }),
        ),
    });
  }

  if (!isShare) {
    items.push({
      multiple: false,
      label: 'File info…',
      icon: ICONS.Help,
      action: () => (dispatch: AppDispatch) => {
        dispatch(openModal({ id: UPLOADER_UPLOAD_FILE_INFO, contextItems }));
      },
    });
  }

  if (contextItems.every(isPausable)) {
    items.push({
      multiple: true,
      label: 'Pause',
      icon: ICONS.Pause,
      action: () => (dispatch: AppDispatch) => {
        dispatch(pauseUploads(contextItems.map((item) => item.id)));
      },
    });
  }

  if (contextItems.every(isPausedUpload)) {
    items.push({
      multiple: true,
      label: 'Resume',
      icon: ICONS.Play,
      action: () => (dispatch: AppDispatch) => {
        dispatch(resumeUploads(contextItems.map((item) => item.id)));
      },
    });
  }

  if (canRemove) {
    items.push({
      multiple: true,
      label: 'Remove',
      icon: ICONS.TrashBin,
      action: () => (dispatch: AppDispatch) => {
        dispatch(removeUploads(contextItems.map((item) => item.id)));
      },
    });
  }

  if (canCancel) {
    items.push({
      multiple: true,
      label: 'Cancel…',
      icon: ICONS.CrossMark,
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          openModal({
            id: UPLOADER_CANCEL_UPLOAD_MODAL,
            contextItems,
          }),
        );
      },
    });
  }
  return {
    items,
  };
};

const UploaderItemContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_UPLOADER_ITEMS,
);

export default UploaderItemContextMenu;
